import { NGXLogger } from 'ngx-logger';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, from, of, firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { AppConstants } from 'src/app/commons/app-constants';
import { Page } from 'src/app/commons/models/page';
import { Product, ProductPhoto } from '../model/product';
import { DetailCacheService } from 'src/app/offline/detail-cache.service';
import { NetworkStatusService } from 'src/app/services/network-status.service';
import { OutboxService, generateProvisionalId } from 'src/app/offline/outbox.service';
import { offlineDb, scopedKey } from 'src/app/offline/offline-db';
import { isOfflineCapableNative } from 'src/app/commons/utils/platform';
import { runDeltaListSync, evictStaleRowFromCache } from 'src/app/offline/delta-merge.util';
import { PrefetchGateService } from 'src/app/offline/prefetch-gate.service';
import { FULL_LIST_MAX, getFullList, saveFullList } from 'src/app/offline/offline-window';


const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};
 
@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private readonly logger = inject(NGXLogger);
  private readonly prefetchGate = inject(PrefetchGateService);



  constructor(
    private http: HttpClient,
    private detailCacheService: DetailCacheService,
    private networkStatusService: NetworkStatusService,
    private outboxService: OutboxService,
  ) { }

  /* getData(): Observable<Product> {
    return this.http.get<Product>(AppConstants.API_URL + 'prodcuts');
  } */

  postData(data: Product): Observable<Product> {
    if (isOfflineCapableNative() && !this.networkStatusService.isOnline) {
      return from(this.createOffline(data));
    }
    return this.http.post<Product>(AppConstants.API_URL + 'api/v1/products', data, { responseType: 'json' });
  }

  // Fabricates a response shaped like a real Product so callers (the form's submit handler,
  // which discards the response body entirely) work unmodified. The actual create is queued
  // for SyncEngineService to replay once back online. Also merges the fabricated product
  // directly into the offline masterCache 'products' list so it shows up immediately in the
  // TU form's autocomplete — the actual point of offline product creation.
  private async createOffline(data: Product): Promise<Product> {
    const clientUuid = uuidv4();
    const provisionalId = generateProvisionalId();
    const provisionalEncodedId = `offline-${clientUuid}`;

    const fakeResponse: any = {
      ...data,
      productId: provisionalId,
      encodedId: provisionalEncodedId,
      // Not part of the real DTO — internal marker so UI code can show an "unsynced" badge.
      _offlineClientUuid: clientUuid,
    };

    await offlineDb.detailCache.put({ key: scopedKey(`product:${provisionalEncodedId}`), data: fakeResponse, fetchedAt: Date.now() });

    const entry = await offlineDb.masterCache.get(scopedKey('products'));
    const list = Array.isArray(entry?.data) ? (entry!.data as any[]) : [];
    await offlineDb.masterCache.put({ key: scopedKey('products'), data: [...list, fakeResponse], fetchedAt: entry?.fetchedAt ?? Date.now() });

    const outboxPayload = { ...data, clientUuid };
    await this.outboxService.enqueue('CREATE_PRODUCT', clientUuid, outboxPayload);
    return fakeResponse;
  }

  getByProductName(name: string): Observable<any | undefined> {
    const encodedName = encodeURIComponent(name);
    const response = this.http.get(AppConstants.API_URL + 'api/v1/check-product-name?name=' + encodedName, httpOptions);
    this.logger.debug(`getByProductName Response is ${JSON.stringify(response)}`)
    return response;
  }

  // <Page<Book>
  /** Deactivate instead of delete: kept in history, hidden from new work. */
  deactivate(id: string): Observable<Product> {
    return this.http.post<Product>(`${AppConstants.API_URL}api/v1/products/${id}/deactivate`, {});
  }

  reactivate(id: string): Observable<Product> {
    return this.http.post<Product>(`${AppConstants.API_URL}api/v1/products/${id}/reactivate`, {});
  }

  getProductsList(httpParams?: HttpParams): Observable<Page<Product>> {
    let url = `${AppConstants.API_URL}api/v1/products/filter?`;
    if (httpParams) {
      url = url + httpParams;
    }
    this.logger.debug(`url is ${url}`)
    return this.http.get<Page<Product>>(url);
  }

  // Delta-sync fetch (Increment 6): on first-ever sync (modifiedSince=null) requests exactly the
  // same size=10&page=0 as before, matching ProductListComponent.loadProducts()'s default query
  // so the merged cache stays a drop-in replacement for what that component reads offline. On a
  // delta sync, size is widened to comfortably capture every row touched since the last sync in
  // one page (a filtered, not a bulk, query — 200 is generous, not a real ceiling in practice).
  private fetchProductsPage(modifiedSince: string | null): Observable<HttpResponse<Page<Product>>> {
    let params = new HttpParams().set('size', modifiedSince ? 200 : 10).set('page', 0);
    if (modifiedSince) params = params.set('modifiedSince', modifiedSince);
    const url = `${AppConstants.API_URL}api/v1/products/filter?${params.toString()}`;
    return this.http.get<Page<Product>>(url, { observe: 'response' });
  }

  // Called by OfflinePrefetchService (Android only) so the list's first page is already cached
  // before the user ever opens Products. Delegates to runDeltaListSync (Increment 6) — first
  // sync ever caches the full response as-is; every sync after that merges only what's changed
  // into the existing cached top-10 window instead of replacing it wholesale.
  // Returns a Promise (resolving once the LIST itself is fetched/cached — never rejects, this
  // is always best-effort) so OfflinePrefetchService's per-area progress checklist can show a
  // meaningful "done" instead of resolving instantly. Per-row detail/photo prefetching below
  // stays fire-and-forget/background, same as everywhere else in this codebase — waiting for
  // every leaf HTTP call before flipping "done" would be truer but is disproportionate for a
  // progress indicator.
  // Phase 2 / D7 (OFFLINE_PLAN.md, 2026-09-29): the whole list (up to FULL_LIST_MAX rows, one request), so
  // this list screen can search / sort / page offline — before, offline showed only its first 10 rows.
  // Best-effort: a failure keeps the last saved list.
  private async saveFullListForOffline(): Promise<void> {
    if (!isOfflineCapableNative()) return;
    try {
      const res = await firstValueFrom(this.http.get<Page<Product>>(`${AppConstants.API_URL}api/v1/products/filter?size=${FULL_LIST_MAX}&page=0`));
      if (Array.isArray(res?.content)) await saveFullList('products', res.content);
    } catch { /* keep the last saved list */ }
  }

  async prefetchDefaultList(): Promise<void> {
    await this.saveFullListForOffline();
    const cacheKey = `productList:${new HttpParams().set('size', 10).set('page', 0).toString()}`;
    const result = await runDeltaListSync<Product>({
      entity: 'products',
      cacheKey,
      fetchPage: (modifiedSince) => this.fetchProductsPage(modifiedSince),
      deletionsEntityType: 'PRODUCT',
      mergeConfig: {
        getId: (p) => p.productId,
        getCreatedDate: (p) => p.createdDate ?? '',
        windowSize: 10,
      },
      http: this.http,
    });
    // A deleted product's cached detail record is otherwise stranded in IndexedDB forever once
    // evicted from the list — see delta-merge.util.ts's evictedRows doc comment. Photo blobs
    // (productPhoto(Detail):${id}, keyed by photo id) can't be derived without a fetch this
    // product can no longer serve, so those are left as a small residual orphan (bounded by the
    // existing 10MB per-file cap) — only the detail record itself is reclaimed here.
    result?.evictedRows?.forEach(row => {
      if (isOfflineCapableNative() && row.encodedId) offlineDb.detailCache.delete(scopedKey(`product:${row.encodedId}`));
    });
    // Every leaf call routed through PrefetchGateService — bounded concurrency, out of Angular's
    // zone, yielding between units. See PrefetchGateService for the unthrottled-fan-out freeze
    // this fixes. Still fire-and-forget.
    for (const row of (result?.page.content ?? [])) {
      const encodedId = row.encodedId;
      if (encodedId) {
        // Raw http.get(), not getProductById()/cacheThrough — see BusinessPartnerService's
        // identical prefetchDefaultList comment for why cacheThrough's stale-cache fallback
        // would otherwise mask the true 404 this self-heal needs to see.
        void this.prefetchGate.run(async () => {
          try {
            const data = await firstValueFrom(this.http.get<Product>(`${AppConstants.API_URL}api/v1/products/${encodedId}`));
            if (isOfflineCapableNative()) {
              await offlineDb.detailCache.put({ key: scopedKey(`product:${encodedId}`), data, fetchedAt: Date.now() });
            }
          } catch (err) {
            if ((err as HttpErrorResponse)?.status === 404) {
              evictStaleRowFromCache<Product>(cacheKey, row.productId, (r) => r.productId);
            }
          }
        });
      }
      this.prefetchProductPhoto(row);
    }
  }

  // Caches the row's display-picture bytes so ProductListComponent can render a real thumbnail
  // instead of a broken-image icon offline. Goes through the /photos list + /photos/{id}/content
  // proxy (never row.displayPhotoUrl directly) — that field is a raw signed Backblaze B2 URL,
  // and a direct browser-side HttpClient fetch of it gets the global interceptor's unconditional
  // withCredentials:true attached to a cross-origin request, which B2 CORS-rejects silently.
  // The /content endpoint re-fetches server-side (not subject to browser CORS) and streams the
  // bytes back same-origin — same fix shape as OfflinePrefetchService.prefetchDocsWithImages'
  // tallyDocImage: pattern.
  private prefetchProductPhoto(row: Product): void {
    if (!row.productId || !row.encodedId || !row.displayPhotoUrl) return;
    const encodedId = row.encodedId;
    const productId = row.productId;
    void this.prefetchGate.run(async () => {
      const photos = await firstValueFrom(
        this.http.get<ProductPhoto[]>(`${AppConstants.API_URL}api/v1/products/${encodedId}/photos`)).catch(() => null);
      if (!photos) return;
      const displayPhoto = photos.find((p: ProductPhoto) => p.isDisplayPicture) ?? photos[0];
      if (displayPhoto) {
        this.cachePhotoBlob(`productPhoto:${productId}`, encodedId, displayPhoto.id);
      }
    });
  }

  // Shared by prefetchProductPhoto (list's display picture) and ProductComponent's photo
  // gallery (every photo on the edit page) — one fetch-and-cache primitive, two call sites.
  // Gated so opening a product with a large gallery doesn't fire every blob download at once.
  private cachePhotoBlob(cacheKey: string, encodedId: string, photoId: number): void {
    void this.prefetchGate.run(async () => {
      try {
        const blob = await firstValueFrom(
          this.http.get(`${AppConstants.API_URL}api/v1/products/${encodedId}/photos/${photoId}/content`, { responseType: 'blob' }));
        await offlineDb.detailCache.put({ key: cacheKey, data: blob, fetchedAt: Date.now() });
      } catch { /* best-effort */ }
    });
  }

  // Called by ProductComponent after a successful (online) photo-gallery fetch on the edit page
  // — opportunistically warms the offline cache for whatever photos were actually viewed, same
  // "cache what's been seen" pattern as prefetchDetailCache elsewhere in this module.
  cachePhotosForProduct(encodedId: string, photos: ProductPhoto[]): void {
    if (!isOfflineCapableNative()) return;
    photos.forEach(photo => this.cachePhotoBlob(`productPhotoDetail:${photo.id}`, encodedId, photo.id));
  }

  getProductsListGlobalFilter(httpParams?: HttpParams): Observable<Page<Product>> {
    let url = `${AppConstants.API_URL}api/v1/products/search?`;
    if (httpParams) {
      url = url + httpParams;
    }
    this.logger.debug(`url is ${url}`)
    return this.http.get<Page<Product>>(url);
  }

  deleteProducts(id: any, forceDelete: boolean = false): Observable<string> {
    let url = `${AppConstants.API_URL}api/v1/products/${id}?forceDelete=${forceDelete}`;
    this.logger.debug(`url is ${url}`)
    return this.http.delete<string>(url);
  }

  deleteProduct(id: any, forceDelete: boolean = false): Observable<string> {
    let url = `${AppConstants.API_URL}api/v1/product/${id}?forceDelete=${forceDelete}`;
    this.logger.debug(`url is ${url}`)
    return this.http.delete<string>(url);
  }

  getProductById(id: string): Observable<Product> {
    const url = `${AppConstants.API_URL}api/v1/products/${id}`;
    this.logger.debug(`[OPAQUE-ID][Product Service] getProductById url is ${url}`);
    // Offline with no saved copy: its row from the full list saved on the phone (2026-09-29).
    return this.detailCacheService.cacheThrough(`product:${id}`, () => this.http.get<Product>(url),
      async () => (await getFullList('products'))?.rows
        .find((r: any) => r?.encodedId === id || String(r?.productId) === String(id)));
  }

  // ProductDto (the response Product rows/getProductById use) only carries displayPhotoUrl, not
  // the full photo gallery — so the edit page's photo list needs its own offline cache, same
  // DetailCacheService write-through/read-fallback pattern as getProductById just above.
  getProductPhotos(encodedId: string): Observable<ProductPhoto[]> {
    const url = `${AppConstants.API_URL}api/v1/products/${encodedId}/photos`;
    return this.detailCacheService.cacheThrough<ProductPhoto[]>(`productPhotos:${encodedId}`, () => this.http.get<ProductPhoto[]>(url));
  }
  updateData(id: string, data: Product): Observable<any> {
    const url = `${AppConstants.API_URL}api/v1/products/${id}`;
    this.logger.debug(`[OPAQUE-ID][Product Service] updateData url is ${url}`);
    return this.http.put<any>(url, data, { responseType: 'json' });
  }

  /** types (2026-10-02): optional, only products of that tally type. */
  search(query: string, types?: 'ROUND' | 'SQUARE'): Observable<Product[]> {
    const url = `${AppConstants.API_URL}api/v1/products/search-query?query=${query}${types ? '&types=' + types : ''}`;
    this.logger.debug(`Url is ${url}`)
      if (!query.trim()) {
        return of([]); // Return an empty observable if query is blank or whitespace
      }
      return this.http.get<Product[]>(url);
    }

  // Queues a photo upload against an already-real (synced) product while offline — mirrors
  // TallysheetListService.queueDocumentUpload's "doc-" prefix branch (already-real master,
  // device just offline). Unlike that method, there's no provisional-id branch here: uploading
  // a photo against a still-unsynced offline-created product (encodedId starting "offline-")
  // is blocked by the caller (ProductComponent) instead, since Product has no idMap-style
  // provisional-to-real id translation the way CREATE_TU does — out of scope for this cut.
  async queueProductPhotoUpload(encodedId: string, file: File): Promise<void> {
    const clientUuid = `photo-${encodedId}-${uuidv4()}`;
    await this.outboxService.enqueue('UPLOAD_PRODUCT_PHOTO', clientUuid, { encodedId, file });
  }

  // Surfaces any not-yet-synced queued photo uploads for this product so ProductComponent can
  // still show them (as a local preview, rebuilt from the File blob Dexie already persisted in
  // the outbox payload) after navigating away and back, before SyncEngineService gets a chance
  // to replay them — without this they'd only exist in the outbox, invisible in the UI.
  async getPendingPhotoUploads(encodedId: string): Promise<Array<{ seq: number; previewUrl: string; fileName: string; rejected: boolean }>> {
    const entries = await offlineDb.outbox
      .filter((e) => e.opType === 'UPLOAD_PRODUCT_PHOTO' && (e.payload as any)?.encodedId === encodedId && e.status !== 'synced')
      .toArray();
    return entries.map((e) => {
      const payload = e.payload as { file: File };
      return {
        seq: e.seq!,
        previewUrl: URL.createObjectURL(payload.file),
        fileName: payload.file.name,
        rejected: e.status === 'rejected',
      };
    });
  }
}
